import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { environment } from 'environments/environment';
import { UserService } from './user.service';
@Injectable({ providedIn: 'root' })
export class FileUploadService {
	private urlPath = '';
	public readonly clientId: string;
	constructor(private httpClient: HttpClient, private userService: UserService) {
		this.clientId = this.userService.currentUserValue.user.clientId;
		this.urlPath = `${environment.apiUrl}file-upload/clients/${this.clientId}/upload`;
	}
	uploadAttachment(clientId: string, formId: string, formType: string, file: File): Observable<number> {
		const chunkSize = 4 * 1024 * 1024;
		const totalChunks = Math.ceil(file.size / chunkSize);
		const progress$ = new Subject<number>();
		let offset = 0;
		let chunkIndex = 0;
		this.httpClient
			.get(`${this.urlPath}/status?formType=${formType}&formId=${formId}&fileName=${encodeURIComponent(file.name)}`)
			.subscribe({
				next: (status: any) => {
					const alreadyUploaded = new Set<number>(status.uploadedBlocks || []);
					if (alreadyUploaded.size > 0) {
						offset = alreadyUploaded.size * chunkSize;
						chunkIndex = alreadyUploaded.size;
						progress$.next(Math.min(Math.round((chunkIndex / totalChunks) * 100), 99));
					}
					const uploadNext = () => {
						if (offset >= file.size) {
							const request: { fileName: string; totalChunks: number; fileHash?: string } = {
								fileName: file.name,
								totalChunks,
								fileHash: (file as any).fileHash,
							};
							this.httpClient
								.post(`${this.urlPath}/attachment?formType=${formType}&formId=${formId}`, request)
								.subscribe({
									complete: () => progress$.complete(),
									error: (err) => progress$.error(err),
								});
							return;
						}
						const chunk = file.slice(offset, offset + chunkSize);
						const formData = new FormData();
						formData.append('file', chunk, file.name);
						this.httpClient
							.post(
								`${this.urlPath}/chunk?formType=${formType}&formId=${formId}&fileName=${encodeURIComponent(
									file.name
								)}&i=${chunkIndex}`,
								formData
							)
							.subscribe({
								next: () => {
									offset += chunkSize;
									chunkIndex++;
									progress$.next(Math.round((chunkIndex / totalChunks) * 100));
									uploadNext();
								},
								error: (err) => progress$.error(err),
							});
					};

					uploadNext();
				},
				error: (err) => progress$.error(err),
			});

		return progress$.asObservable();
	}
	async uploadAttachments(
		clientId: string,
		formId: string,
		formType: string,
		files: File[]
	): Promise<Subject<number>[]> {
		const chunkSize = 4 * 1024 * 1024;
		const progressSubjects: Subject<number>[] = [];
		const completeRequests: { fileName: string; totalChunks: number; fileHash?: string }[] = [];
		const uploads: Promise<void>[] = [];

		for (const file of files) {
			const totalChunks = Math.ceil(file.size / chunkSize);
			const progress$ = new Subject<number>();
			progressSubjects.push(progress$);

			const uploadPromise = new Promise<void>(async (resolve, reject) => {
				let offset = 0;
				let chunkIndex = 0;

				try {
					const status = await firstValueFrom(
						this.httpClient.get<{ uploadedBlocks: number[] }>(
							`${this.urlPath}/status?formType=${formType}&formId=${formId}&fileName=${encodeURIComponent(file.name)}`
						)
					);
					const alreadyUploaded = new Set(status.uploadedBlocks || []);

					const uploadNext = async () => {
						if (offset >= file.size) {
							completeRequests.push({ fileName: file.name, totalChunks, fileHash: (file as any).fileHash });
							progress$.next(100);
							progress$.complete();
							resolve();
							return;
						}

						if (alreadyUploaded.has(chunkIndex)) {
							offset += chunkSize;
							chunkIndex++;
							progress$.next(Math.round((chunkIndex / totalChunks) * 100));
							uploadNext();
							return;
						}

						const chunk = file.slice(offset, offset + chunkSize);
						const formData = new FormData();
						formData.append('file', chunk, file.name);

						try {
							await firstValueFrom(
								this.httpClient.post(
									`${this.urlPath}/chunk?formType=${formType}&formId=${formId}&fileName=${encodeURIComponent(
										file.name
									)}&i=${chunkIndex}`,
									formData
								)
							);

							offset += chunkSize;
							chunkIndex++;
							progress$.next(Math.round((chunkIndex / totalChunks) * 100));
							uploadNext();
						} catch (err) {
							progress$.error(err);
							reject(err);
						}
					};

					uploadNext();
				} catch (err) {
					progress$.error(err);
					reject(err);
				}
			});

			uploads.push(uploadPromise);
		}
		await Promise.allSettled(uploads);
		if (completeRequests.length > 0) {
			await firstValueFrom(
				this.httpClient.post(`${this.urlPath}/attachments?formType=${formType}&formId=${formId}`, completeRequests)
			);
		}
		return progressSubjects;
	}
}
